import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, {
  setInvalidTokenHandler,
  setTokenRefreshHandler,
} from "../shared/api.jsx";
import * as jwtDecodeModule from "jwt-decode";
import { useInactivityTracker } from "../hooks/useInactivityTracker.js";
import InactivityWarningModal from "../components/InactivityWarningModal.jsx";

const jwtDecode = jwtDecodeModule.default || jwtDecodeModule.jwtDecode;

const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REFRESH_TOKEN: "REFRESH_TOKEN",
  UPDATE_USER: "UPDATE_USER",
  SET_LOADING: "SET_LOADING",
  CLEAR_ERROR: "CLEAR_ERROR",
};

const initialState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  permissions: [],
  role: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return { ...state, isLoading: true, error: null };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        permissions: action.payload.user.permissions || [],
        role: action.payload.user.role,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        accessToken: null,
        permissions: [],
        role: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, isLoading: false };
    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        accessToken: action.payload.accessToken,
        user: action.payload.user,
        permissions: action.payload.user.permissions || [],
        role: action.payload.user.role,
        isAuthenticated: true,
      };
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload.user,
        permissions: action.payload.user.permissions || [],
        role: action.payload.user.role,
      };
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const isLoggingOutRef = useRef(false);
  const logoutRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Set up invalid token handler (always use latest logout via ref)
  useEffect(() => {
    setInvalidTokenHandler(() => {
      const out = logoutRef.current;
      if (out) out({ bypassDraftPrompt: true });
    });
  }, []);

  // On mount, check for access token and decode user info
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: decoded,
            accessToken: token,
          },
        });
      } catch (e) {
        localStorage.removeItem("accessToken");
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    } else {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    setIsInitialized(true);
  }, []);

  // Login function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const data = await api.post("/auth/login", { email, password });
      if (data.code === "INVALID_CREDENTIALS") {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: "Invalid email or password",
        });
        return { success: false, error: "Invalid email or password" };
      }
      if (data.success) {
        const { tokens, user } = data.data;
        localStorage.setItem("accessToken", tokens.accessToken);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user,
            accessToken: tokens.accessToken,
          },
        });
        return { success: true };
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: data.message || "Login failed",
        });
        return { success: false, error: data.message };
      }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message || "Network error. Please try again.",
      });
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  // Logout function
  const logout = useCallback(
    async (opts = {}) => {
      if (isLoggingOutRef.current) return;
      const bypassDraftPrompt = opts.bypassDraftPrompt === true;

      if (!bypassDraftPrompt) {
        const hasLeadDraft = localStorage.getItem("lead-form-draft");
        const hasQuoteDraft = localStorage.getItem("quote-form-draft");

        if (hasLeadDraft || hasQuoteDraft) {
          const confirmed = window.confirm(
            "You have unsaved drafts. They will be saved locally and restored when you return. Continue with logout?"
          );
          if (!confirmed) {
            return;
          }
        }
      }

      isLoggingOutRef.current = true;

      const currentLocation = location;

      try {
        await api
          .post("/auth/logout", {}, { skipAuthRedirect: true })
          .catch(() => {});
      } finally {
        localStorage.removeItem("accessToken");
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        navigate("/login", { state: { from: currentLocation }, replace: true });
        setTimeout(() => {
          isLoggingOutRef.current = false;
        }, 1000);
      }
    },
    [navigate, location]
  );

  logoutRef.current = logout;

  const refreshToken = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const run = (async () => {
      try {
        const data = await api.post(
          "/auth/refresh",
          {},
          { skipAuthRedirect: true }
        );
        if (data.success && data.data?.accessToken) {
          const { accessToken, user: userFromApi } = data.data;
          localStorage.setItem("accessToken", accessToken);
          const user = userFromApi || jwtDecode(accessToken);
          dispatch({
            type: AUTH_ACTIONS.REFRESH_TOKEN,
            payload: {
              accessToken,
              user,
            },
          });
          return accessToken;
        }
        await logout({ bypassDraftPrompt: true });
        return null;
      } catch (error) {
        const msg = String(error?.message || "");
        if (
          /\b401\b/.test(msg) ||
          /Unauthorized/i.test(msg) ||
          /Session expired/i.test(msg)
        ) {
          await logout({ bypassDraftPrompt: true });
        }
        return null;
      }
    })();
    refreshInFlightRef.current = run.finally(() => {
      refreshInFlightRef.current = null;
    });
    return refreshInFlightRef.current;
  }, [logout]);

  useEffect(() => {
    setTokenRefreshHandler(() => refreshToken());
    return () => setTokenRefreshHandler(null);
  }, [refreshToken]);

  useEffect(() => {
    if (!state.isAuthenticated || !state.accessToken) {
      return undefined;
    }
    let cancelled = false;
    let timeoutId;

    const schedule = () => {
      try {
        const expMs = jwtDecode(state.accessToken).exp * 1000;
        const rawDelay = Math.max(
          10_000,
          expMs - Date.now() - 5 * 60 * 1000
        );
        const delay = Math.min(rawDelay, 2147483647);
        timeoutId = window.setTimeout(() => {
          if (!cancelled) refreshToken();
        }, delay);
      } catch {
        /* ignore */
      }
    };

    schedule();

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const t = localStorage.getItem("accessToken");
      if (!t) return;
      try {
        const expMs = jwtDecode(t).exp * 1000;
        if (expMs - Date.now() < 5 * 60 * 1000) {
          refreshToken();
        }
      } catch {
        /* ignore */
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [state.isAuthenticated, state.accessToken, refreshToken]);

  const hasPermission = (permission) => state.permissions.includes(permission);
  const hasAnyPermission = (permissions) =>
    permissions.some((p) => state.permissions.includes(p));
  const hasAllPermissions = (permissions) =>
    permissions.every((p) => state.permissions.includes(p));
  const hasRole = (role) => state.role === role;
  const hasAnyRole = (roles) => roles.includes(state.role);
  const isAdmin = hasAnyRole(["admin", "super_admin"]);
  const isSuperAdmin = hasRole("super_admin");
  const isAgent = hasAnyRole(["agent", "admin", "super_admin"]);
  const clearError = () => dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

  // Create context value first to ensure it's available before Provider renders
  const value = {
    user: state.user,
    accessToken: state.accessToken,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitialized,
    error: state.error,
    permissions: state.permissions,
    role: state.role,
    login,
    logout,
    refreshToken,
    clearError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    isAgent,
  };

  // Inactivity tracking handlers (defined after value to avoid dependency issues)
  const handleInactivity = useCallback(() => {
    setShowInactivityWarning(true);
  }, []);

  const handleStayLoggedIn = useCallback(() => {
    setShowInactivityWarning(false);
  }, []);

  const handleInactivityLogout = useCallback(async () => {
    setShowInactivityWarning(false);
    await logout();
  }, [logout]);

  // Initialize inactivity tracker (only when authenticated)
  const inactivityTracker = useInactivityTracker(
    handleInactivity,
    handleStayLoggedIn,
    state.isAuthenticated
  );

  // Reset inactivity timer when warning is closed
  useEffect(() => {
    if (!showInactivityWarning && state.isAuthenticated) {
      inactivityTracker.resetTimer();
    }
  }, [showInactivityWarning, state.isAuthenticated, inactivityTracker]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <InactivityWarningModal
        open={showInactivityWarning}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleInactivityLogout}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
